// ModifierSystem — the single effects pipeline. Facilities, court upgrades
// and active event effects all declare `Modifier`s; Economy/Demand resolve
// stats through here instead of duplicating string-switches, and the UI
// renders any modifier with `formatModifier`.
//
// This is a pure derivation module (like DemandSystem): no EventBus
// subscription, no lifecycle, safe to import from UI components. Config
// files import ONLY TYPES from here, so there is no runtime import cycle.

import { useGameStore, type Court } from '@/store/gameStore'
import type { Building } from '@/config/buildingConfig'
import type { GameEffect } from '@/config/eventsConfig'
// These configs import ONLY types from this file, so the cycle is erased
// at compile time and there is no runtime circular dependency.
import { FACILITY_REGISTRY, getFacilitySpec } from '@/config/facilityRegistry'
import { COURT_UPGRADES } from '@/config/upgradesConfig'

/** Stats a modifier can move. Extend the union as new systems consume it. */
export type ModifierStat =
  | 'income.total'          // multiplier on all service income
  | 'income.court_rental'   // per-court multiplier (court scope)
  | 'income.retail'         // retail/cafeteria income
  | 'income.class'          // classes & academy income
  | 'demand.all'            // add: flat demand factor; mult: demand multiplier
  | 'satisfaction'          // points; feeds the daily review roll (/200)
  | 'court.premium_price'   // €/h added on top of the court price
  | 'court.condition_decay' // multiplier on weekly court wear
  | 'expenses.maintenance'  // multiplier on weekly upkeep (solar panels)
  | 'reputation.weekly'     // add: weekly reputation drip (press room)

export type ModifierOp = 'add' | 'mult'

export interface Modifier {
  stat: ModifierStat
  op: ModifierOp
  value: number
  /** 'club' (default) applies club-wide; 'court' applies to the court that
   *  owns the source (e.g. an installed court upgrade). */
  scope?: 'club' | 'court'
}

export interface SourcedModifier extends Modifier {
  sourceId: string   // 'facility:cafe@3' | 'upgrade:led_lighting' | 'effect:<id>'
  sourceName: string // Spanish, for tooltips/breakdowns
  courtId?: string   // set when scope === 'court'
}

/** Minimal state slice the collector needs (subset of the game store). */
export interface ModifierSnapshot {
  buildings: Building[]
  courts: Court[]
  courtUpgrades: Record<string, string[]>
  activeEffects: GameEffect[]
  /** Estrellas del Club: each earned star is a permanent income boost */
  clubStars?: string[]
  /** Facility specializations: buildingId -> chosen spec id */
  buildingSpecs?: Record<string, string>
}

function snapshot(): ModifierSnapshot {
  const s = useGameStore.getState()
  return {
    buildings: s.buildings,
    courts: s.courts,
    courtUpgrades: s.courtUpgrades,
    activeEffects: s.activeEffects,
    clubStars: s.clubStars,
    buildingSpecs: s.buildingSpecs,
  }
}

/** Permanent income boost per earned club star */
export const STAR_INCOME_MULT = 1.02

/** GameEffect (random events) → modifier stat mapping. */
const EFFECT_STAT_MAP: Partial<Record<GameEffect['type'], ModifierStat>> = {
  income_modifier: 'income.total',
  demand_modifier: 'demand.all',
  expense_modifier: 'expenses.maintenance',
}

/**
 * Walk every modifier source in the club:
 * - Facilities: the registry level's modifiers. `add` values are weighted by
 *   the building's condition (a crumbling cafe pulls less demand) — this
 *   preserves the legacy `level × condition/100` demand behavior exactly.
 * - Court upgrades: catalog modifiers, stamped with their court id.
 * - Active effects: event multipliers mapped onto engine stats.
 */
export function collectModifiers(state: ModifierSnapshot = snapshot()): SourcedModifier[] {
  const out: SourcedModifier[] = []

  for (const building of state.buildings) {
    const def = FACILITY_REGISTRY[building.type]
    if (!def) continue
    const level = def.levels.find(l => l.level === (building.level ?? 1))
    if (!level) continue
    const conditionWeight = (building.condition ?? 100) / 100
    for (const mod of level.modifiers) {
      out.push({
        ...mod,
        value: mod.op === 'add' ? mod.value * conditionWeight : mod.value,
        sourceId: `facility:${building.type}@${building.level}`,
        sourceName: def.name,
      })
    }

    // Terminal specialization (permanent, chosen at max level)
    const specId = state.buildingSpecs?.[building.id]
    const spec = specId ? getFacilitySpec(building.type, specId) : undefined
    if (spec) {
      for (const mod of spec.modifiers) {
        out.push({
          ...mod,
          value: mod.op === 'add' ? mod.value * conditionWeight : mod.value,
          sourceId: `spec:${building.type}:${spec.id}`,
          sourceName: `${def.name} · ${spec.name}`,
        })
      }
    }
  }

  for (const [courtId, upgradeIds] of Object.entries(state.courtUpgrades)) {
    for (const upgradeId of upgradeIds) {
      const upgrade = COURT_UPGRADES.find(u => u.id === upgradeId)
      if (!upgrade) continue
      for (const mod of upgrade.modifiers) {
        out.push({
          ...mod,
          scope: 'court',
          courtId,
          sourceId: `upgrade:${upgradeId}`,
          sourceName: upgrade.name,
        })
      }
    }
  }

  for (const effect of state.activeEffects) {
    const stat = EFFECT_STAT_MAP[effect.type]
    if (!stat) continue
    out.push({
      stat,
      op: 'mult',
      value: effect.value,
      sourceId: `effect:${effect.id}`,
      sourceName: effect.source,
    })
  }

  for (const star of state.clubStars ?? []) {
    out.push({
      stat: 'income.total',
      op: 'mult',
      value: STAR_INCOME_MULT,
      sourceId: `star:${star}`,
      sourceName: 'Estrella del Club',
    })
  }

  return out
}

/** Does a modifier apply to this stat query? Club scope always applies;
 *  court scope only when querying that court. */
function applies(mod: SourcedModifier, stat: ModifierStat, courtId?: string): boolean {
  if (mod.stat !== stat) return false
  if (mod.scope === 'court') return courtId !== undefined && mod.courtId === courtId
  return true
}

export function sumAdds(mods: SourcedModifier[], stat: ModifierStat, courtId?: string): number {
  return mods.reduce((sum, m) => sum + (m.op === 'add' && applies(m, stat, courtId) ? m.value : 0), 0)
}

export function productMults(mods: SourcedModifier[], stat: ModifierStat, courtId?: string): number {
  return mods.reduce((mult, m) => mult * (m.op === 'mult' && applies(m, stat, courtId) ? m.value : 1), 1)
}

/** Resolve a stat: (base + Σadds) × Πmults. */
export function resolveStat(
  stat: ModifierStat,
  base: number,
  opts: { courtId?: string; mods?: SourcedModifier[] } = {},
): number {
  const mods = opts.mods ?? collectModifiers()
  return (base + sumAdds(mods, stat, opts.courtId)) * productMults(mods, stat, opts.courtId)
}

/** Every modifier touching a stat, for tooltips/breakdown UI. */
export function getStatBreakdown(stat: ModifierStat, courtId?: string, mods?: SourcedModifier[]): SourcedModifier[] {
  return (mods ?? collectModifiers()).filter(m => applies(m, stat, courtId))
}

/** Club-wide satisfaction points: club-scoped adds plus the court-scoped
 *  adds averaged over all courts (one upgraded court out of many shouldn't
 *  count as if every court had it). */
export function getClubSatisfactionBonus(state: ModifierSnapshot = snapshot()): number {
  const mods = collectModifiers(state)
  let clubAdds = 0
  let courtAdds = 0
  for (const m of mods) {
    if (m.stat !== 'satisfaction' || m.op !== 'add') continue
    if (m.scope === 'court') courtAdds += m.value
    else clubAdds += m.value
  }
  const courtCount = state.courts.length
  return clubAdds + (courtCount > 0 ? courtAdds / courtCount : 0)
}

// --- Spanish labels -------------------------------------------------------

const STAT_LABELS: Record<ModifierStat, string> = {
  'income.total': 'ingresos',
  'income.court_rental': 'ingresos de pista',
  'income.retail': 'ingresos de tienda y cafetería',
  'income.class': 'ingresos de clases',
  'demand.all': 'demanda',
  'satisfaction': 'satisfacción',
  'court.premium_price': 'precio',
  'court.condition_decay': 'desgaste',
  'expenses.maintenance': 'gastos de mantenimiento',
  'reputation.weekly': 'reputación semanal',
}

/** Generic Spanish pill label for any modifier. Mirrors the legacy pill
 *  wording so the UI reads the same after the refactor. */
export function formatModifier(mod: Modifier): string {
  const label = STAT_LABELS[mod.stat] ?? mod.stat

  if (mod.op === 'mult') {
    const pct = Math.round(Math.abs(mod.value - 1) * 100)
    if (mod.stat === 'court.condition_decay') {
      return mod.value < 1 ? `${pct}% menos desgaste` : `+${pct}% desgaste`
    }
    return mod.value >= 1 ? `+${pct}% ${label}` : `-${pct}% ${label}`
  }

  // add
  if (mod.stat === 'court.premium_price') return `+${mod.value}€/hora`
  if (mod.stat === 'demand.all') return `+${Math.round(mod.value * 100)}% demanda`
  if (mod.stat === 'satisfaction') return `+${mod.value} satisfacción`
  return `+${mod.value} ${label}`
}
